import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeller } from '../context/SellerContext';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../context/CategoryContext';
import { getCategorySettings } from '../services/api';

function SellerHeader({ cartCount }) {
  const navigate = useNavigate();
  const { subdomain, shopName, seller: initialSellerInfo } = useSeller(); 
  const { isAuthenticated, logout, user } = useAuth();
  const { categories: ctxCategories } = useCategories();
  
  const [sortedCategories, setSortedCategories] = useState([]);
  const [sellerInfo, setSellerInfo] = useState(initialSellerInfo || {});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile Sidebar State

  // Update seller info if initialSellerInfo changes
  useEffect(() => {
    if (initialSellerInfo) {
      setSellerInfo(initialSellerInfo);
    }
  }, [initialSellerInfo]);

  // CategoryContext에서 활성 카테고리 (displayOrder 순 정렬됨)
  useEffect(() => {
    if (ctxCategories.length > 0) {
      const SPECIAL = [
        { name: '베스트 상품', value: 'best', displayOrder: -99 },
        { name: '신상품', value: 'new', displayOrder: -98 },
        { name: '추천 상품', value: 'recommended', displayOrder: -97 }
      ];

      const dbCats = ctxCategories.map(cat => ({
        name: cat.name || cat.title,
        value: cat.categoryValue,
        hot: cat.isHot || false,
        displayOrder: cat.displayOrder || 999,
      }));

      const hotCat = dbCats.find(c => c.hot);
      const regularCats = dbCats.filter(c => !c.hot);

      const combined = [
        ...(hotCat ? [{ ...hotCat, displayOrder: -100 }] : []),
        ...SPECIAL,
        ...regularCats,
      ];
      combined.sort((a, b) => a.displayOrder - b.displayOrder);
      setSortedCategories(combined);
    }
  }, [ctxCategories]);

  const logoUrl = sellerInfo?.logoUrl;
  const displayName = sellerInfo?.shopName || shopName || "FlowerShop";

  const handleCategoryClick = (value) => {
    navigate(`/products?category=${value}`);
  };

  return (
    <>
        <header className="fixed top-0 z-50 w-full border-b border-solid border-[#e7f3eb] dark:border-[#1d3324] bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md font-display">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10 py-4 flex items-center justify-between">
            
            {/* Mobile Hamburger Menu Description */}
            <div className="md:hidden mr-4">
                <button 
                  onClick={() => setIsSidebarOpen(true)}
                  className="bg-transparent border-none p-0 flex items-center text-[#0d1b12] dark:text-white"
                >
                    <span className="material-symbols-outlined text-3xl">menu</span>
                </button>
            </div>

            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              {logoUrl ? (
                <img src={logoUrl} alt={displayName} className="h-10 w-auto object-contain" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-contrast">
                  <span className="material-symbols-outlined text-2xl font-bold">local_florist</span>
                </div>
              )}
              <h1 className="text-xl font-extrabold tracking-tight text-[#0d1b12] dark:text-white">{displayName}</h1>
            </div>
            <div className="hidden md:flex flex-1 justify-center gap-8">
              {sortedCategories.slice(0, 5).map((cat) => (
                <button 
                  key={cat.value} 
                  onClick={() => handleCategoryClick(cat.value)}
                  className={`text-sm font-semibold hover:text-primary-strong transition-colors bg-transparent border-none cursor-pointer ${cat.value === 'best' || cat.value === 'hotdeal' ? 'text-primary-strong' : 'text-[#0d1b12] dark:text-white'}`}
                >
                  {cat.name}
                </button>
              ))}
              
              {/* All Categories Dropdown */}
              <div className="relative group z-50">
                <button className="flex items-center gap-1 text-sm font-semibold hover:text-primary-strong transition-colors bg-transparent border-none cursor-pointer text-[#0d1b12] dark:text-white">
                  <span className="material-symbols-outlined text-xl leading-none flex items-center">menu</span>
                  전체 카테고리
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white dark:bg-[#162a1c] rounded-xl shadow-xl border border-[#e7f3eb] dark:border-[#1d3324] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 p-4 grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                  <div className="absolute -top-2 left-0 w-full h-4 bg-transparent"></div> {/* Bridge for hover */}
                  {sortedCategories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => handleCategoryClick(cat.value)}
                      className={`text-xs text-left px-2 py-1.5 rounded hover:bg-[#f0f9f2] dark:hover:bg-[#1d3324] hover:text-primary-strong transition-colors bg-transparent border-none cursor-pointer truncate ${cat.value === 'best' || cat.value === 'hotdeal' ? 'font-bold text-primary-strong' : 'text-gray-600 dark:text-gray-300'}`}
                      title={cat.name}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="hidden sm:flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                {isAuthenticated ? (
                  <>
                    <span className="text-primary-strong font-bold">{user?.name || user?.username}님</span>
                    <span className="h-3 w-[1px] bg-gray-300 dark:bg-gray-700"></span>
                    <button 
                      onClick={() => {
                        logout();
                        navigate('/');
                      }}
                      className="hover:text-primary-strong bg-transparent border-none cursor-pointer p-0 font-medium text-xs text-gray-500 dark:text-gray-400 font-display"
                    >
                      로그아웃
                    </button>
                    <span className="h-3 w-[1px] bg-gray-300 dark:bg-gray-700"></span>
                    <Link to="/mypage" className="hover:text-primary-strong">마이페이지</Link>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="hover:text-primary-strong">로그인</Link>
                    <span className="h-3 w-[1px] bg-gray-300 dark:bg-gray-700"></span>
                    <Link to="/signup" className="hover:text-primary-strong">회원가입</Link>
                  </>
                )}
                <span className="h-3 w-[1px] bg-gray-300 dark:bg-gray-700"></span>
                <Link to="/inquiry" className="hover:text-primary-strong">고객센터</Link>
              </div>
              <div className="flex gap-2">
                <Link to="/cart" className="flex size-10 items-center justify-center rounded-lg bg-[#e7f3eb] dark:bg-[#1d3324] text-[#0d1b12] dark:text-white hover:bg-primary hover:text-primary-contrast dark:hover:text-primary-contrast transition-colors relative">
                  <span className="material-symbols-outlined">shopping_cart</span>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-bold">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Sidebar Overlay & Drawer */}
        {isSidebarOpen && (
            <div className="fixed inset-0 z-[60] flex md:hidden">
                {/* Overlay */}
                <div 
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={() => setIsSidebarOpen(false)}
                />

                {/* Drawer */}
                <div className="relative w-[80%] max-w-[320px] bg-white dark:bg-[#0d1b12] h-full shadow-2xl flex flex-col animate-slide-in-left">
                    {/* Drawer Header */}
                    <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                        <h2 className="text-lg font-bold text-[#0d1b12] dark:text-white">메뉴</h2>
                        <button 
                            onClick={() => setIsSidebarOpen(false)}
                            className="bg-transparent border-none p-1 text-gray-500 hover:text-red-500 transition-colors"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* Drawer Content - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-5">
                        
                        {/* Auth Section */}
                        <div className="mb-8">
                            {isAuthenticated ? (
                                <div className="bg-gray-50 dark:bg-[#162a1c] p-4 rounded-xl">
                                    <p className="text-lg font-bold text-[#0d1b12] dark:text-white mb-1">
                                        <span className="text-primary-strong">{user?.name || user?.username}</span>님
                                    </p>
                                    <p className="text-xs text-gray-500 mb-4">반갑습니다!</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button 
                                            onClick={() => {
                                                navigate('/mypage');
                                                setIsSidebarOpen(false);
                                            }}
                                            className="bg-white dark:bg-[#0d1b12] border border-gray-200 dark:border-gray-700 py-2 rounded-lg text-xs font-bold hover:border-primary hover:text-primary-strong transition-colors"
                                        >
                                            마이페이지
                                        </button>
                                        <button 
                                            onClick={() => {
                                                logout();
                                                setIsSidebarOpen(false);
                                                navigate('/');
                                            }}
                                            className="bg-white dark:bg-[#0d1b12] border border-gray-200 dark:border-gray-700 py-2 rounded-lg text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                        >
                                            로그아웃
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-sm text-gray-500 mb-4">로그인하고 혜택을 받아보세요!</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Link 
                                            to="/login"
                                            onClick={() => setIsSidebarOpen(false)}
                                            className="bg-primary text-primary-contrast py-3 rounded-xl text-center font-bold text-sm hover:opacity-90 transition-opacity"
                                        >
                                            로그인
                                        </Link>
                                        <Link 
                                            to="/signup"
                                            onClick={() => setIsSidebarOpen(false)}
                                            className="bg-white border border-gray-200 text-gray-700 py-3 rounded-xl text-center font-bold text-sm hover:bg-gray-50 transition-colors"
                                        >
                                            회원가입
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Categories Section */}
                        <div className="mb-8">
                            <h3 className="font-bold text-[#0d1b12] dark:text-white mb-4 flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary-strong">category</span>
                                카테고리
                            </h3>
                            <div className="space-y-1">
                                {sortedCategories.map((cat) => (
                                    <button
                                        key={cat.value}
                                        onClick={() => {
                                            handleCategoryClick(cat.value);
                                            setIsSidebarOpen(false);
                                        }}
                                        className={`w-full text-left py-3 px-3 rounded-lg flex items-center justify-between transition-colors ${
                                            cat.value === 'best' || cat.value === 'hotdeal' 
                                            ? 'bg-primary/5 text-primary-strong font-bold'
                                            : 'hover:bg-gray-50 dark:hover:bg-[#162a1c] text-gray-600 dark:text-gray-300'
                                        }`}
                                    >
                                        {cat.name}
                                        <span className="material-symbols-outlined text-sm text-gray-400">chevron_right</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Other Links */}
                        <div className="border-t border-gray-100 dark:border-gray-800 pt-6">
                            <Link 
                                to="/inquiry"
                                onClick={() => setIsSidebarOpen(false)}
                                className="flex items-center gap-3 text-gray-600 dark:text-gray-300 py-2 hover:text-primary-strong transition-colors"
                            >
                                <span className="material-symbols-outlined">support_agent</span>
                                <span className="font-medium">고객센터문의</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>
  );
}

export default SellerHeader;
